import React, { useEffect, useRef, useState } from "react";
import { Search, TrendingUp, Loader2 } from "lucide-react";
import { TID } from "@/lib/testIds";
import { searchStocks } from "@/lib/api";

export default function SearchBar({ onSelect, compact = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!q) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await searchStocks(q);
        if (!cancelled) setResults(r);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  const submit = () => {
    const first = results[0];
    if (first) {
      onSelect(first.ticker);
      setQ("");
      setOpen(false);
    } else if (q.trim()) {
      onSelect(q.trim().toUpperCase());
      setQ("");
      setOpen(false);
    }
  };

  const sizing = compact
    ? "px-3 py-2 text-sm rounded-md"
    : "px-4 py-3.5 text-base rounded-lg";

  return (
    <div ref={wrapRef} className={`relative w-full ${compact ? "" : "max-w-2xl"}`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" strokeWidth={1.5} />
          <input
            ref={inputRef}
            data-testid={compact ? TID.searchInput : TID.heroSearchInput}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="輸入股票代碼或公司名稱  例如：AAPL、TSLA、NVDA、2330.TW、台積電"
            className={`w-full bg-zinc-950 border-2 border-zinc-800 focus:border-blue-500 text-zinc-100 pl-10 pr-4 font-mono placeholder:text-zinc-600 outline-none transition-all ${sizing}`}
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin" />
          )}
        </div>
        <button
          data-testid={compact ? TID.searchButton : TID.heroSearchButton}
          onClick={submit}
          className={`bg-blue-600 hover:bg-blue-500 text-white font-semibold ${sizing} shadow-[0_0_20px_rgba(59,130,246,0.25)] transition-colors whitespace-nowrap`}
        >
          {compact ? "搜尋" : "開始分析"}
        </button>
      </div>

      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 mt-2 bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden z-50 shadow-2xl">
          {results.slice(0, 8).map((r) => (
            <button
              key={r.ticker}
              data-testid={TID.suggestionItem}
              onClick={() => {
                onSelect(r.ticker);
                setQ("");
                setOpen(false);
              }}
              className="flex items-center justify-between w-full px-4 py-3 hover:bg-zinc-900 transition-colors border-b border-zinc-800 last:border-b-0 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-md bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-blue-400" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-white text-sm">{r.ticker}</span>
                    <span className="text-xs text-zinc-500">{r.industry}</span>
                  </div>
                  <div className="text-xs text-zinc-400">
                    {r.name_zh ? `${r.name_zh}` : r.name}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm text-zinc-200">
                  {r.price != null ? r.price : "—"}
                </div>
                {r.change_pct != null ? (
                  <div
                    className={`font-mono text-xs ${
                      r.change_pct >= 0 ? "text-emerald-400" : "text-red-400"
                    }`}
                  >
                    {r.change_pct >= 0 ? "+" : ""}
                    {r.change_pct.toFixed(2)}%
                  </div>
                ) : (
                  <div className="font-mono text-xs text-zinc-500">即時</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
